/* eslint-disable */
/* tslint:disable */
/* prettier-ignore */
interface TrackOptionsBase {
  sendUserIfExists: boolean
}

interface TrackLiteOptions extends TrackOptionsBase {
  lite: true
  sendInstance: boolean
  sendUser: boolean
  sendUserIfExists: boolean
  sendBrowser: boolean
}

export type TrackOptions =
  | ({ lite: false } & TrackOptionsBase)
  | TrackLiteOptions

export class GnarSpec {

  constructor(private gnar: { track(props: { eventName: string, [k: string]: any }, options?: TrackOptions): Promise<void> }) {}

  /**
   * Generic button clicked (non-CTA) Base events for button-related events
   * @param displayContent - Button label text as rendered
   * @param metadata - Additional button context
   * @param objectId - Optional. Not used in eventName/functionName. If not
   *                   provided, it will be generated based on the object and
   *                   action. A unique id/name of an object which performed event
   *                   activity (upon which activity was performed). Purpose of
   *                   `object`, `objectId`, `action` and `client` props is to
   *                   standardize the way `eventName` is formed. A unique id/name
   *                   of an object which performed event activity (upon which
   *                   activity was performed). Purpose of `object`, `objectId`,
   *                   `action` and `client` props is to standardize the way
   *                   `eventName` is formed.
   * @param pageId - Page ID field of the page from Contentful Page entry on
   *                 which event happened.
   * @param pageSlug - Url path of the page on which event happened. It excludes
   *                   the leading and trailing slash and query parameters.
   */
  buttonClick(
    displayContent: string,
    metadata: { placement: 'Header' | 'Section' | 'Modal' | 'Hero' | 'FloatingHeader', isDisabled?: boolean },
    objectId: string,
    pageId: string,
    pageSlug: string
  ): Promise<void> {
    return this.gnar.track({
      action: 'click',
      eventName: 'superhumanFunnel/button-click',
      object: 'button',
      displayContent,
      metadata,
      objectId,
      pageId,
      pageSlug
    })
  }

  /**
   * Component show event Base for component-related events
   * @param componentId - Unique identifier for the component instance
   * @param componentType - Type of component being tracked
   * @param objectId - Optional. Not used in eventName/functionName. If not
   *                   provided, it will be generated based on the object and
   *                   action. A unique id/name of an object which performed event
   *                   activity (upon which activity was performed). Purpose of
   *                   `object`, `objectId`, `action` and `client` props is to
   *                   standardize the way `eventName` is formed. A unique id/name
   *                   of an object which performed event activity (upon which
   *                   activity was performed). Purpose of `object`, `objectId`,
   *                   `action` and `client` props is to standardize the way
   *                   `eventName` is formed.
   * @param pageId - Page ID field of the page from Contentful Page entry on
   *                 which event happened.
   * @param pageSlug - Url path of the page on which event happened. It excludes
   *                   the leading and trailing slash and query parameters.
   */
  componentShow(
    componentId: string,
    componentType: 'Header' | 'Section' | 'Modal' | 'Hero',
    objectId: string,
    pageId: string,
    pageSlug: string
  ): Promise<void> {
    return this.gnar.track({
      action: 'show',
      eventName: 'superhumanFunnel/component-show',
      object: 'component',
      componentId,
      componentType,
      objectId,
      pageId,
      pageSlug
    })
  }

  /**
   * CTA button clicked Base for CTA button events Base events for button-related events
   * @param ctaType - Logical CTA kind, e.g., superhumanExtensionInstall,
   *                  signupButtonWRedirect, customLinkButton, textSubmitButton,
   *                  uploadFileButton etc.
   * @param displayContent - Button label text as rendered
   * @param metadata - Additional button context
   * @param objectId - Optional. Not used in eventName/functionName. If not
   *                   provided, it will be generated based on the object and
   *                   action. A unique id/name of an object which performed event
   *                   activity (upon which activity was performed). Purpose of
   *                   `object`, `objectId`, `action` and `client` props is to
   *                   standardize the way `eventName` is formed. A unique id/name
   *                   of an object which performed event activity (upon which
   *                   activity was performed). Purpose of `object`, `objectId`,
   *                   `action` and `client` props is to standardize the way
   *                   `eventName` is formed.
   * @param pageId - Page ID field of the page from Contentful Page entry on
   *                 which event happened.
   * @param pageSlug - Url path of the page on which event happened. It excludes
   *                   the leading and trailing slash and query parameters.
   * @param targetUrl - Navigation target (if applicable)
   */
  ctaButtonClick(
    ctaType: string,
    displayContent: string,
    metadata: { placement: 'Header' | 'Section' | 'Modal' | 'Hero' | 'FloatingHeader', styleVariant: 'primary' | 'secondary' | 'tertiary' | 'text', isDisabled?: boolean },
    objectId: string,
    pageId: string,
    pageSlug: string,
    targetUrl?: string
  ): Promise<void> {
    return this.gnar.track({
      action: 'click',
      eventName: 'superhumanFunnel/ctaButton-click',
      object: 'ctaButton',
      ctaType,
      displayContent,
      metadata,
      objectId,
      pageId,
      pageSlug,
      targetUrl
    })
  }

  /**
   * Page redirect event Base events for page-related events
   * @param objectId - Optional. Not used in eventName/functionName. If not
   *                   provided, it will be generated based on the object and
   *                   action. A unique id/name of an object which performed event
   *                   activity (upon which activity was performed). Purpose of
   *                   `object`, `objectId`, `action` and `client` props is to
   *                   standardize the way `eventName` is formed. A unique id/name
   *                   of an object which performed event activity (upon which
   *                   activity was performed). Purpose of `object`, `objectId`,
   *                   `action` and `client` props is to standardize the way
   *                   `eventName` is formed.
   * @param pageId - Page ID field of the page from Contentful Page entry on
   *                 which event happened.
   * @param pageSlug - Url path of the page on which event happened. It excludes
   *                   the leading and trailing slash and query parameters.
   * @param redirectUrl - Redirect url
   * @param referrer - Current page referrer
   * @param url - Full page url string
   * @param isCustomPage - When true, indicates that the rendered page was a custom
   *                       page.
   * @param isStaticFallback - When true, indicates that the rendered page was a static
   *                           fallback.
   */
  pageRedirect(
    objectId: string,
    pageId: string,
    pageSlug: string,
    redirectUrl: string,
    referrer: string,
    url: string,
    isCustomPage?: boolean,
    isStaticFallback?: boolean
  ): Promise<void> {
    return this.gnar.track({
      action: 'redirect',
      eventName: 'superhumanFunnel/page-redirect',
      object: 'page',
      objectId,
      pageId,
      pageSlug,
      redirectUrl,
      referrer,
      url,
      isCustomPage,
      isStaticFallback
    })
  }

  /**
   * Page show Event Base events for page-related events
   * @param objectId - Optional. Not used in eventName/functionName. If not
   *                   provided, it will be generated based on the object and
   *                   action. A unique id/name of an object which performed event
   *                   activity (upon which activity was performed). Purpose of
   *                   `object`, `objectId`, `action` and `client` props is to
   *                   standardize the way `eventName` is formed. A unique id/name
   *                   of an object which performed event activity (upon which
   *                   activity was performed). Purpose of `object`, `objectId`,
   *                   `action` and `client` props is to standardize the way
   *                   `eventName` is formed.
   * @param pageId - Page ID field of the page from Contentful Page entry on
   *                 which event happened.
   * @param pageSlug - Url path of the page on which event happened. It excludes
   *                   the leading and trailing slash and query parameters.
   * @param referrer - Current page referrer
   * @param url - Full page url string
   * @param isCustomPage - When true, indicates that the rendered page was a custom
   *                       page.
   * @param isStaticFallback - When true, indicates that the rendered page was a static
   *                           fallback.
   */
  pageShow(
    objectId: string,
    pageId: string,
    pageSlug: string,
    referrer: string,
    url: string,
    isCustomPage?: boolean,
    isStaticFallback?: boolean
  ): Promise<void> {
    return this.gnar.track({
      action: 'show',
      eventName: 'superhumanFunnel/page-show',
      object: 'page',
      objectId,
      pageId,
      pageSlug,
      referrer,
      url,
      isCustomPage,
      isStaticFallback
    })
  }
}
/* eslint-enable */
/* tslint:enable */
